import React, { useState, useEffect } from 'react';

import Navbar from '../../Components/Navbar/Navbar';

import {
  Layout,
  Col,
  Row,
  Space,
  Input,
  Table,
  Tabs,
  DatePicker,
} from 'antd';

import { ProductOutlined } from '@ant-design/icons';
import { UseGetRequest } from '../../Utils/api/ApiRequests';
import { TAB_KEYS, TRANSACTION_TABS } from '../../Utils/api/DropdownfilterApi';
import { formatDate, capitalizeEachWord, TypeTag } from '../../Utils/Common';

const { Search } = Input;
const { RangePicker } = DatePicker;

const TransactionHistory = () => {
  const [activeTab, setActiveTab] = useState(TAB_KEYS.PROSHOP);
  const [isLoading, setIsLoading] = useState(false);
  const [transactionData, setTransactionData] = useState([]);

  const [filterConfig, setFilterConfig] = useState({
    term: '',
    page: 1,
    page_size: 10,
    date_from: '',
    date_to: '',
    store: 'PROSHOP',
  });

  const customSpinner = (
    <ProductOutlined style={{ fontSize: 24, color: '#005E30' }} spin />
  );

  const columns = [
    {
      title: 'Member Name',
      key: 'member_name',
      // Sort using the combined full name string
      sorter: (a, b) => {
        const nameA = `${a.first_name || ''} ${a.middle_name || ''} ${a.last_name || ''}`.trim();
        const nameB = `${b.first_name || ''} ${b.middle_name || ''} ${b.last_name || ''}`.trim();
        return nameA.localeCompare(nameB);
      },
      render: (_, record) => {
        const { first_name, middle_name, last_name } = record;
        
        // Filter out empty strings or whitespaces so you don't get double spaces
        const fullName = [first_name, middle_name, last_name]
          .map(name => name?.trim())
          .filter(Boolean)
          .join(' ');

        return fullName ? capitalizeEachWord(fullName) : '—';
      },
    },
    {
      title: 'Transaction Date',
      dataIndex: 'added_on',
      key: 'added_on',
      sorter: (a, b) =>
        new Date(a.added_on) - new Date(b.added_on),
      render: (text) => (text ? formatDate(text) : '—'),
    },
    {
      title: 'Sales Invoice No.',
      dataIndex: 'si_number',
      key: 'si_number',
      sorter: (a, b) =>
        a.sales_invoice_number?.localeCompare(b.sales_invoice_number),
      render: (text) =>
      text ? String(text).padStart(12, '0') : '—',
    },
    {
      title: 'Payment Type',
      dataIndex: 'payment_type',
      key: 'payment_type',
      align: 'center',
      render: (text) => (text ? TypeTag(text) : '—'),
    },
    {
      title: 'Reference No.',
      dataIndex: 'reference_number',
      key: 'reference_number',
      render: (text) => (text == null ? '' : String(text)),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      sorter: (a, b) => parseFloat(a.amount) - parseFloat(b.amount),
      render: (text) =>
        text
          ? Number(text).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : '0.00',
    },
  ];

  async function fetchTransactionData() {
    setIsLoading(true);

    const response = await UseGetRequest('transactions/getAll', filterConfig);

    if (response?.data) {
      setTransactionData(response?.data?.message?.data ?? []);
    } else {
      setTransactionData([]);
    }
    setIsLoading(false);
  }

  const handleTableChange = (pagination) => {
    setFilterConfig((prev) => ({
      ...prev,
      page: parseInt(pagination.current),
      page_size: pagination.pageSize,
    }));
  };

  const handleSearch = (value) => {
    setFilterConfig((prev) => ({ ...prev, term: value || '', page: 1 }));
  };

  const handleSearchEmpty = (e) => {
    if (e.target.value === '') {
      setFilterConfig((prev) => ({ ...prev, term: '', page: 1 }));
    }
  };

  const handleDateRange = (_, dateStrings) => {
    setFilterConfig((prev) => ({
      ...prev,
      date_from: dateStrings[0] || '',
      date_to: dateStrings[1] || '',
      page: 1,
    }));
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setTransactionData([]);
    setFilterConfig((prev) => ({
      ...prev,
      page: 1,
      store: TRANSACTION_TABS.find((tab) => tab.key === key).store,
    }));
  };

  useEffect(() => {
    fetchTransactionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig, activeTab]);

  const tabItems = TRANSACTION_TABS.map(({ key, label }) => ({
    key,
    label: <span style={{ padding: '0 8px', fontWeight: 500 }}>{label}</span>,
    children: (
      <Table
        columns={columns}
        dataSource={transactionData}
        rowKey={(record) => record.id ?? record.sales_invoice_number}
        loading={{ indicator: customSpinner, spinning: isLoading }}
        scroll={{ y: 460, x: 'max-content' }}
        pagination={{
          defaultPageSize: 10,
          current: filterConfig.page,
          pageSize: filterConfig.page_size,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
      />
    ),
  }));

  return (
    <Layout className="page">
      <Navbar navKey={'transaction-history'} />
      <Layout className="content-container overflow-y">
        <Row className="custom-padding responsive-tab-inline">
          <Col span={15} className="header-container">
            TRANSACTION HISTORY
          </Col>
          <Col
            span={9}
            className="mt-2 responsive-tab-width"
            align="end"
          >
            <Space className="responsive-inline">
              <RangePicker
                onChange={handleDateRange}
                className="responsive-header-input"
              />
              <Search
                placeholder="Search Member / Invoice"
                size="medium"
                onSearch={handleSearch}
                onChange={handleSearchEmpty}
                className="responsive-header-input"
              />
            </Space>
          </Col>
        </Row>

        <Row className="page-content" style={{ padding: '0 20px 20px 20px' }}>
          <Col span={24} className="responsive-overflow-auto">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={tabItems}
              style={{ 
                background: 'white', 
                borderRadius: '12px', 
                padding: '20px 24px',
                minHeight: '70vh',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
              tabBarStyle={{
                marginBottom: '20px',
              }}
              renderTabBar={() => (
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  background: '#f0f4f0',
                  borderRadius: '8px',
                  padding: '4px',
                  width: 'fit-content',
                  marginBottom: '20px',
                }}>
                  {TRANSACTION_TABS.map(({ key, label }) => (
                    <div
                      key={key}
                      onClick={() => handleTabChange(key)}
                      style={{
                        padding: '8px 28px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: activeTab === key ? 600 : 400,
                        fontSize: '0.9rem',
                        background: activeTab === key ? 'var(--dominant-color)' : 'transparent',
                        color: activeTab === key ? 'white' : 'var(--dominant-color)',
                        transition: 'all 0.2s ease',
                        userSelect: 'none',
                      }}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              )}
            />
          </Col>
        </Row>
      </Layout>
    </Layout>
  );
};

export default TransactionHistory;
